import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import api from "../api/http";
import type { ApiError } from "../api/http";
import type { MessageResponse } from "../api/types";
import { useResendVerification } from "../hooks/useResendVerification";
import { useResendCooldown } from "../hooks/useResendCooldown";
import {
  clearPendingVerificationEmail,
  getPendingVerificationEmail,
} from "../utils/pendingVerification";
import { AuthLayout } from "../components/auth/AuthLayout";
import {
  authInput,
  authLink,
  authMuted,
  authPrimaryBtn,
} from "../components/auth/authStyles";

type Status = "loading" | "success" | "error";
type ErrorKind = "missing" | "expired";

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  const [errorKind, setErrorKind] = useState<ErrorKind>("expired");
  const [serverMessage, setServerMessage] = useState("");
  // Pre-filled with the address stored at registration/join/login-403 so the
  // user doesn't have to re-type (and possibly mistype) it.
  const [resendEmail, setResendEmail] = useState(getPendingVerificationEmail);
  const [resendOk, setResendOk] = useState(false);
  const resend = useResendVerification();
  const { cooldown, start: startCooldown } = useResendCooldown("verify");

  useEffect(() => {
    const uid = searchParams.get("uid");
    const token = searchParams.get("token");

    if (!uid || !token) {
      setStatus("error");
      setErrorKind("missing");
      return;
    }

    (async () => {
      try {
        const response = await api.post<MessageResponse>("/api/v1/auth/verify-email/", {
          uid,
          token,
        });
        setStatus("success");
        setServerMessage(response.detail);
        clearPendingVerificationEmail();
      } catch (err) {
        const apiErr = err as ApiError;
        setStatus("error");
        setErrorKind("expired");
        setServerMessage(apiErr.detail || "");
      }
    })();
  }, [searchParams]);

  const handleResend = async () => {
    if (!resendEmail || cooldown > 0 || resend.isPending) return;
    try {
      await resend.mutateAsync({ email: resendEmail });
    } catch {
      // Endpoint is enumeration-safe — even on rate-limit hits we surface the
      // same "sent" affordance so we don't leak whether the email was real.
    }
    setResendOk(true);
    startCooldown();
  };

  return (
    <AuthLayout>
      <div className="text-center">
        {status === "loading" && (
          <>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500 dark:border-white/10 dark:border-t-blue-400" />
            <p className={`mt-3 text-sm ${authMuted}`}>{t("auth.verify.loading")}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-600 dark:bg-emerald-500/15 dark:ring-emerald-500/30 dark:text-emerald-300">
              <Check size={22} />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{t("auth.verify.successTitle")}</h2>
            {serverMessage ? (
              <p className={`mt-2 text-sm ${authMuted}`}>{serverMessage}</p>
            ) : null}
            <Link
              to="/login"
              className={`mt-6 inline-block ${authPrimaryBtn} max-w-[12rem]`}
            >
              {t("auth.verify.signinLink")}
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 ring-1 ring-rose-200 text-rose-600 dark:bg-rose-500/15 dark:ring-rose-500/30 dark:text-rose-300">
              <X size={22} />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{t("auth.verify.errorTitle")}</h2>
            <p className={`mt-2 text-sm ${authMuted}`}>
              {errorKind === "missing"
                ? t("auth.verify.errorBodyMissing")
                : t("auth.verify.errorBodyExpired")}
            </p>

            <div className="mt-6 rounded-2xl bg-slate-50 px-4 py-4 text-left ring-1 ring-slate-200 dark:bg-white/[0.03] dark:ring-white/10">
              <label htmlFor="resend-email" className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                {t("auth.verify.resendPrompt")}
              </label>
              <input
                id="resend-email"
                type="email"
                autoComplete="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@example.com"
                className={authInput}
              />
              <button
                type="button"
                onClick={handleResend}
                disabled={!resendEmail || cooldown > 0 || resend.isPending}
                className={`mt-2 ${authPrimaryBtn}`}
              >
                {resend.isPending
                  ? t("auth.verify.resending")
                  : cooldown > 0
                    ? t("auth.verify.resendCooldown", { seconds: cooldown })
                    : t("auth.verify.resendButton")}
              </button>
              {resendOk && cooldown > 0 ? (
                <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-300">{t("auth.verify.resendSent")}</p>
              ) : null}
            </div>

            <Link to="/register" className={`mt-4 inline-block text-sm ${authLink}`}>
              {t("auth.verify.registerLink")}
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
